'use client';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PromoBannerType } from '@nutralab/api';
import { CarouselV2 } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './FooterBanners.module.scss';

import { BannerHtml } from '@/app/_components/BannerHtml';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';

const CAROUSEL_AUTOPLAY_INTERVAL_MS = 5000;
const BANNERS_PER_VIEW = 2;

type FooterBannersProps = React.HTMLAttributes<HTMLDivElement>;

/** The carousel of the CMS footer banners on the home page. */
export function FooterBanners({ className, ...props }: FooterBannersProps): React.ReactElement | null {
    const { t } = useTranslation();
    const { promo_banners } = useSiteConfig();

    const banners = useMemo(() => promo_banners.filter((b) => b.type === PromoBannerType.FOOTER), [promo_banners]);

    if (banners.length === 0) {
        return null;
    }

    return (
        <div className={classNames(styles.wrapper, className)} {...props}>
            {banners.length > BANNERS_PER_VIEW ? (
                <CarouselV2
                    className={classNames(styles.carousel, 'mx-auto md:mx-0')}
                    itemsPerView={BANNERS_PER_VIEW}
                    autoplayInterval={CAROUSEL_AUTOPLAY_INTERVAL_MS}
                    labels={{ previous: t('carousel.previous'), next: t('carousel.next') }}
                >
                    {banners.map((banner, i) => (
                        <BannerHtml key={i} html={banner.html} className={styles.banner} />
                    ))}
                </CarouselV2>
            ) : (
                <div className={classNames(styles.banners, banners.length === 1 && styles['count-1'])}>
                    {banners.map((banner, i) => (
                        <div key={i} className={classNames(styles.cell, 'mx-auto md:mx-0')}>
                            <BannerHtml html={banner.html} className={styles.banner} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
